import { useTranslate } from "ra-core";
import { useMemo } from "react";

import { DEAL_STATUSES } from "../deals/dealStatuses";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { toExcelDate, toLabels } from "./cells";
import { customFieldColumns } from "./customFieldColumns";
import type { SheetColumn } from "./types";

/** Spreadsheet columns of opportunities, in the order of the export and the template. */
export const useDealColumns = (): SheetColumn<Deal>[] => {
  const translate = useTranslate();
  const { dealCategories, dealStages, customFields } =
    useConfigurationContext();

  return useMemo(() => {
    const statuses = DEAL_STATUSES.map((status) => ({
      ...status,
      label: translate(`resources.deals.statuses.${status.value}`),
    }));
    const label = (field: string) =>
      translate(`resources.deals.fields.${field}`);
    const columns: SheetColumn<Deal>[] = [
      {
        key: "name",
        header: label("name"),
        group: "identity",
        kind: "text",
        required: true,
        width: 32,
        example: "Séminaire de printemps",
        get: (deal) => deal.name,
      },
      {
        key: "company",
        header: label("company_id"),
        group: "identity",
        kind: "text",
        width: 28,
        example: "Banque Lémanique SA",
        hint: translate("crm.spreadsheet.hints.company"),
        get: (deal, lookups) =>
          deal.company_id != null
            ? lookups.companies.get(deal.company_id)
            : undefined,
      },
      {
        key: "contacts",
        header: label("contact_ids"),
        group: "identity",
        kind: "text",
        width: 28,
        exportOnly: true,
        get: (deal, lookups) =>
          deal.contact_ids
            ?.map((id) => lookups.contacts.get(id))
            .filter(Boolean)
            .join(", "),
      },
      {
        key: "category",
        header: label("category"),
        group: "prospecting",
        kind: "choice",
        options: dealCategories,
        example: dealCategories[0]?.label,
        get: (deal) => toLabels(deal.category, dealCategories),
      },
      {
        key: "stage",
        header: label("stage"),
        group: "prospecting",
        kind: "choice",
        options: dealStages,
        example: dealStages[0]?.label,
        get: (deal) => toLabels(deal.stage, dealStages),
      },
      {
        key: "status",
        header: label("status"),
        group: "prospecting",
        kind: "choice",
        options: statuses,
        example: statuses[0]?.label,
        get: (deal) => toLabels(deal.status, statuses),
      },
      {
        key: "amount",
        header: label("amount"),
        group: "prospecting",
        kind: "currency",
        example: 15000,
        get: (deal) => deal.amount,
      },
      {
        key: "expected_closing_date",
        header: label("expected_closing_date"),
        group: "prospecting",
        kind: "date",
        get: (deal) => toExcelDate(deal.expected_closing_date),
      },
      {
        key: "description",
        header: label("description"),
        group: "follow_up",
        kind: "long_text",
        width: 40,
        get: (deal) => deal.description,
      },
      {
        key: "sales_id",
        header: translate("resources.companies.fields.sales_id"),
        group: "follow_up",
        kind: "text",
        exportOnly: true,
        get: (deal, lookups) =>
          deal.sales_id != null ? lookups.sales.get(deal.sales_id) : undefined,
      },
      {
        key: "created_at",
        header: translate("resources.companies.fields.created_at"),
        group: "follow_up",
        kind: "date",
        exportOnly: true,
        get: (deal) => toExcelDate(deal.created_at),
      },
      ...customFieldColumns<Deal>(customFields.deals ?? [], {
        yes: translate("ra.boolean.true"),
        no: translate("ra.boolean.false"),
      }),
    ];
    return columns;
  }, [translate, dealCategories, dealStages, customFields]);
};
